/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-90 — the rules behind the create-item wizard, with no React in them
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Everything the six screens decide lives here: which steps a category earns,
 * what each step demands before it will let you past, and how a finished draft
 * becomes the `CreateItemPayload` the API has always taken.
 *
 * It is a separate module for one reason — it is the only part that can be
 * tested. Jest in this repo compiles `.spec.ts` on a `node` environment
 * (`jest.config.ts`), so a component test is not on the table; pure rules are.
 *
 * The payload built here is byte-for-byte the one the single-page form built
 * before: same fields, same trims, same optionals. This work order moved the
 * questions around, not what gets stored.
 */

import type { CreateItemPayload, DefaultUom, ItemCategory, TypedMoneyBasis } from "../../api/types";
import { estimateTargetPriceEur } from "../../utils/priceFloor";
import { isVesselItemCategory } from "../../utils/vesselItemCategory";

export type WizardStep = "category" | "name" | "photo" | "price" | "extras" | "review";

/** Screen order. The done screen is not a step — you cannot navigate back to it. */
export const WIZARD_STEPS: readonly WizardStep[] = [
  "category",
  "name",
  "photo",
  "price",
  "extras",
  "review",
] as const;

/** Short names for the progress rail — six segments have no room for a sentence. */
export const STEP_LABELS: Record<WizardStep, string> = {
  category: "What is it",
  name: "Name",
  photo: "Photo",
  price: "Price",
  extras: "Details",
  review: "Review",
};

export interface ItemDraft {
  categoryId: string;
  newCategoryLabel: string;
  newCategoryVatPercent: string;
  newCategoryTargetMarginPercent: string;
  name: string;
  subCategoryId: string;
  colorId: string;
  defaultUom: DefaultUom;
  isPerishable: boolean;
  defaultPurchaseUnitCostEur: string;
  /**
   * G-121 S3 (OD-138/OD-140): which side of IVA "What you pay" was typed on.
   * Null until the person picks; the item stores only the before-IVA cost,
   * which the server works out at the category's rate.
   */
  defaultPurchaseUnitCostBasis: TypedMoneyBasis | null;
  defaultSaleUnitPriceEur: string;
  reorderThreshold: string;
  sku: string;
  barcode: string;
  diameterCm: string;
  depthCm: string;
}

export const EMPTY_DRAFT: ItemDraft = {
  categoryId: "",
  newCategoryLabel: "",
  // G-30: a new category must carry a VAT rate; 21 is the general default.
  newCategoryVatPercent: "21",
  newCategoryTargetMarginPercent: "50",
  name: "",
  subCategoryId: "",
  colorId: "",
  defaultUom: "UNIT",
  isPerishable: false,
  defaultPurchaseUnitCostEur: "",
  defaultPurchaseUnitCostBasis: null,
  defaultSaleUnitPriceEur: "",
  reorderThreshold: "",
  sku: "",
  barcode: "",
  diameterCm: "",
  depthCm: "",
};

/**
 * What picking a category settles on the draft's behalf.
 *
 * Flowers are counted by the stem and are perishable — the single-page form
 * pre-filled both from an effect, and the wizard keeps the same two answers.
 * Both stay overridable: the unit is a chip row on the price step and the
 * perishable flag is a statement with a Change beside it.
 */
export function categoryDefaults(
  categoryCode: string | undefined,
): { defaultUom: DefaultUom; isPerishable: boolean } | null {
  if (categoryCode === "FLOWER") {
    return { defaultUom: "STEM", isPerishable: true };
  }
  return null;
}

/** Buckets and vases carry a size; nothing else is asked for one. */
export function showsVesselDimensions(categoryCode: string | undefined): boolean {
  return categoryCode !== undefined && isVesselItemCategory(categoryCode);
}

/** Flower type only exists for categories the taxonomy has subcategories for. */
export function showsSubCategory(subCategoryCount: number): boolean {
  return subCategoryCount > 0;
}

function parseOptional(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : NaN;
}

function outOfRange(value: number | undefined, min: number, max: number): boolean {
  if (value === undefined) return false;
  return Number.isNaN(value) || value < min || value > max;
}

/**
 * What stops a step from advancing, in the words the florist sees.
 *
 * The single-page form threw these three messages from inside `handleSubmit`,
 * so a missing price surfaced only after every other field had been filled and
 * the form submitted. Here each one is checked by the step that asks for it.
 */
export function validateStep(
  step: WizardStep,
  draft: ItemDraft,
  /**
   * Whether the picked category is the "create a new one" sentinel. Passed in
   * rather than compared here: `CategoryPicker` owns that sentinel, it lives in
   * a `.tsx` file, and jest's `moduleFileExtensions` has no `tsx` — importing
   * it would make this module untestable to buy nothing.
   */
  creatingNewCategory: boolean,
): string | null {
  if (step === "category") {
    if (!draft.categoryId) return "Pick a category.";
    if (creatingNewCategory) {
      if (!draft.newCategoryLabel.trim()) return "Enter a name for the new category.";
      const vat = parseOptional(draft.newCategoryVatPercent);
      if (vat === undefined || outOfRange(vat, 0, 100)) {
        return "Enter a VAT rate between 0 and 100 for the new category.";
      }
      // G-45: bounded BELOW 100 — the price formula divides by (1 - m/100),
      // so 100 is an infinite price and above it the price goes negative.
      const margin = parseOptional(draft.newCategoryTargetMarginPercent);
      if (margin === undefined || Number.isNaN(margin) || margin < 0 || margin >= 100) {
        return "Enter a target margin of 0 or more and under 100 for the new category.";
      }
    }
    return null;
  }

  if (step === "name") {
    return draft.name.trim() ? null : "Give the item a name before continuing.";
  }

  if (step === "price") {
    const cost = parseOptional(draft.defaultPurchaseUnitCostEur);
    if (outOfRange(cost, 0, Number.MAX_SAFE_INTEGER)) {
      return "Purchase cost must be a non-negative number.";
    }
    if (cost !== undefined && draft.defaultPurchaseUnitCostBasis === null) {
      return "Say whether what you pay is before IVA or includes IVA.";
    }
    const price = parseOptional(draft.defaultSaleUnitPriceEur);
    if (outOfRange(price, 0, Number.MAX_SAFE_INTEGER)) {
      return "Selling price must be a non-negative number.";
    }
    // An item may be created before its first priced delivery. Until then it
    // has no automatic selling price and cannot be used where one is required.
    return null;
  }

  if (step === "extras") {
    const reorder = parseOptional(draft.reorderThreshold);
    if (outOfRange(reorder, 0, Number.MAX_SAFE_INTEGER)) {
      return "Reorder threshold must be a non-negative number.";
    }
    if (outOfRange(parseOptional(draft.diameterCm), 0, 10_000)) {
      return "Diameter (cm) must be between 0 and 10000.";
    }
    if (outOfRange(parseOptional(draft.depthCm), 0, 10_000)) {
      return "Depth (cm) must be between 0 and 10000.";
    }
    return null;
  }

  return null;
}

/**
 * Every refusal the draft still carries, with the step that owns it.
 *
 * The per-step checks are not enough on their own: the progress rail lets you
 * jump back to any step you have reached, so a florist can enter an invalid
 * selling price on step 4 and jump straight to the review without passing the
 * check that guards it. A blank price is allowed until a priced delivery exists.
 */
export function firstRefusal(
  draft: ItemDraft,
  creatingNewCategory: boolean,
): { step: WizardStep; message: string } | null {
  for (const step of WIZARD_STEPS) {
    const message = validateStep(step, draft, creatingNewCategory);
    if (message) return { step, message };
  }
  return null;
}

/**
 * The price the category's own VAT and target margin imply for this cost.
 *
 * A SUGGESTION, and labelled as one on screen. `estimateTargetPriceEur` is the
 * client-side mirror of the server's formula and its module is explicit that
 * these numbers decide nothing — the florist types over it freely, and the
 * server remains the authority on every price it enforces.
 *
 * Returns null when there is nothing honest to suggest: no cost yet, no
 * category, or a margin the formula cannot use — or (G-121 S3) a cost typed
 * WITH IVA: the formula takes a before-IVA cost, and taking the IVA out is
 * the server's job alone, so with "Includes IVA" picked the price is worked
 * out after saving instead.
 */
export function suggestedSalePriceEur(
  draft: ItemDraft,
  category: ItemCategory | undefined,
): number | null {
  if (!category) return null;
  if (draft.defaultPurchaseUnitCostBasis !== "BEFORE_IVA") return null;
  const cost = parseOptional(draft.defaultPurchaseUnitCostEur);
  if (cost === undefined || Number.isNaN(cost) || cost <= 0) return null;
  const vat = Number(category.vatRatePercent);
  const margin = Number(category.targetMarginPercent);
  if (!Number.isFinite(vat) || !Number.isFinite(margin)) return null;
  const gross = estimateTargetPriceEur(cost, vat, margin);
  if (gross === null || !Number.isFinite(gross)) return null;
  // Up to the nearest 5 cents: a shop price, not a spreadsheet number.
  return Math.ceil(gross * 20) / 20;
}

/**
 * The draft as the API takes it.
 *
 * `categoryId` is passed in rather than read off the draft because a brand-new
 * category only gets its id once it has been created, one call earlier.
 */
export function buildCreateItemPayload(
  draft: ItemDraft,
  resolvedCategoryId: string,
  resolvedCategoryCode: string | undefined,
): CreateItemPayload {
  const price = parseOptional(draft.defaultSaleUnitPriceEur);
  return {
    name: draft.name.trim(),
    categoryId: resolvedCategoryId,
    defaultUom: draft.defaultUom,
    sku: draft.sku.trim() || undefined,
    barcode: draft.barcode.trim() || undefined,
    isPerishable: draft.isPerishable,
    // Optional since G-13: real costs come from delivery batches, and manual
    // adjustments seed from the latest real batch — this only covers the time
    // before the first delivery.
    defaultPurchaseUnitCostEur: parseOptional(draft.defaultPurchaseUnitCostEur),
    ...(parseOptional(draft.defaultPurchaseUnitCostEur) !== undefined &&
    draft.defaultPurchaseUnitCostBasis !== null
      ? { defaultPurchaseUnitCostBasis: draft.defaultPurchaseUnitCostBasis }
      : {}),
    defaultSaleUnitPriceEur: price,
    subCategoryId: draft.subCategoryId || undefined,
    reorderThreshold: parseOptional(draft.reorderThreshold),
    colorId: draft.colorId || undefined,
    ...(showsVesselDimensions(resolvedCategoryCode)
      ? {
          diameterCm: parseOptional(draft.diameterCm),
          depthCm: parseOptional(draft.depthCm),
        }
      : {}),
  };
}
